import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { 
  Box, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Button, TextField, CircularProgress, Grid, IconButton 
} from '@mui/material';
// Dodaj te ikony:
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import useTeamCompetitionData from './useTeamCompetitionData';
import { auth } from './Firebase';
import { InputBase } from '@mui/material';
import { onAuthStateChanged } from 'firebase/auth';
import axios from 'axios';
import { useCompetitions } from './CompetitionContext';

const LiveSimulation = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { updateCompetition, refreshCompetitions } = useCompetitions();

  // Wykorzystujemy istniejący hook do pobrania danych o lidze
  const { teams, matches, name, group, loading, error } = useTeamCompetitionData(id);

  const [localMatches, setLocalMatches] = useState({});
  const [globalMinute, setGlobalMinute] = useState('');
  const [userId, setUserId] = useState(null);
  const [isSimulating, setIsSimulating] = useState(false);

  // Zabezpieczamy odpowiednią nazwę ligi do nagłówka
  const leagueNameDisplay = useMemo(() => {
    if (name) return `${name}${group && group.trim() !== '' ? ` (${group})` : ''}`;
    return 'Liga';
  }, [name, group]);

  // Autoryzacja Firebase (wymagana dla zapisu symulacji do bazy)
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setUserId(user ? user.uid : null);
    });
    return unsubscribe;
  }, []);

  // Inicjalizacja formularza zaplanowanymi meczami
 useEffect(() => {
    if (matches && matches.length > 0) {
      // Tworzymy zmienną lokalną tylko tutaj, wewnątrz useEffect
      const filtered = matches.filter(m => m.status && m.status.toLowerCase() === 'planned');
      
      const initialMatchesState = {};
      filtered.forEach(m => {
        initialMatchesState[m.matchId || m.id] = {
          homeTeamName: m.homeTeamName,
          awayTeamName: m.awayTeamName,
          homeGoals: 0,
          awayGoals: 0,
          homeRed: 0,
          awayRed: 0,
          minute: 0
        };
      });
      setLocalMatches(initialMatchesState);
    }
  }, [matches]);

  // Obsługa zmian w inputach tabeli
  const handleIncrement = (matchId, field, delta) => {
    setLocalMatches(prev => ({
      ...prev,
      [matchId]: {
        ...prev[matchId],
        [field]: Math.max(0, parseInt(prev[matchId][field] || 0) + delta)
      }
    }));
  };
  
  const handleInputChange = (matchId, field, value) => {
    setLocalMatches(prev => ({
      ...prev,
      [matchId]: {
        ...prev[matchId],
        [field]: value
      }
    }));
  };

  // Ustawienie minuty dla wszystkich meczów jednocześnie
  const applyGlobalMinute = () => {
    if (globalMinute === '') return;
    
    setLocalMatches(prev => {
      const nextState = { ...prev };
      Object.keys(nextState).forEach(matchId => {
        nextState[matchId].minute = globalMinute;
      });
      return nextState;
    });
  };

  // Wysyłka danych do backendu
  const handleSimulate = async () => {
    if (!userId) {
      alert('Musisz być zalogowany, aby utworzyć symulację LIVE.');
      return;
    }

    // Budujemy obiekt liveData: Map<Integer, int[]>
    const liveData = {};
    Object.keys(localMatches).forEach(matchId => {
      const m = localMatches[matchId];
      // Wysyłamy tylko mecze, w których wpisano bramki
      if (m.homeGoals !== '' && m.awayGoals !== '') {
        liveData[parseInt(matchId)] = [
          parseInt(m.homeGoals) || 0,
          parseInt(m.awayGoals) || 0,
          parseInt(m.homeRed) || 0,
          parseInt(m.awayRed) || 0,
          parseInt(m.minute) || 0
        ];
      }
    });

    if (Object.keys(liveData).length === 0) {
      alert('Wprowadź wyniki dla przynajmniej jednego meczu!');
      return;
    }

    setIsSimulating(true);

    try {
      const payload = {
        competitionId: parseInt(id),
        iterations: 10000,
        userId: '',
        conditions: [], // Zostawiamy puste dla symulacji LIVE
        liveData: liveData
      };

      console.log('Payload wysyłany do symulacji LIVE:', payload);
      
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/simulation/create`, payload);
      const simulationId = response.data.simulationId;

      // 1. Zostawiamy aktualizacje kontekstu
      if (updateCompetition) updateCompetition(parseInt(id), simulationId);
      if (refreshCompetitions) await refreshCompetitions();
	  
	  alert('Symulacja LIVE została pomyślnie utworzona!');

    } catch (err) {
      console.error('Błąd podczas uruchamiania symulacji LIVE:', err);
      alert('Wystąpił błąd podczas symulacji. Sprawdź konsolę.');
    } finally {
      setIsSimulating(false);
    }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}><CircularProgress /></Box>;
  if (error) return <Typography color="error" align="center">Błąd ładowania danych.</Typography>;

  
  return (
    <Box sx={{ maxWidth: 1000, margin: '0 auto', p: 2 }}>
      <Helmet>
        <title>Symulacja LIVE - {leagueNameDisplay} | Symuliga</title>
      </Helmet>

      <Typography variant="h4" component="h1" sx={{ mb: 1, fontWeight: 'bold', color: '#fff', textAlign: 'center' }}>
        Symulacja LIVE: {leagueNameDisplay}
      </Typography>
      
      <Typography variant="body2" sx={{ mb: 4, color: '#aaa', textAlign: 'center', maxWidth: 800, margin: '0 auto 30px auto', lineHeight: 1.5 }}>
        Wprowadź aktualne wyniki, minuty meczów i czerwone kartki. Puste pola bramek oznaczają, że dla danego meczu silnik przeprowadzi symulację od 0. minuty.
      </Typography>

      {Object.keys(localMatches).length === 0 ? (
			<Typography>Brak meczów.</Typography>
			) : (
		  <>
          {/* USTAWIENIA GLOBALNE */}
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, mb: 3 }}>
            <TextField 
              label="Minuta dla wszystkich" 
              variant="outlined" 
              size="small" 
              type="number"
              value={globalMinute}
              onChange={(e) => setGlobalMinute(e.target.value)}
              sx={{ width: '200px', backgroundColor: '#2c2c2c', borderRadius: 1 }}
              InputProps={{ style: { color: '#fff' } }}
              InputLabelProps={{ style: { color: '#aaa' } }}
            />
            <Button 
              variant="outlined" 
              color="info" 
              startIcon={<AccessTimeIcon />}
              onClick={applyGlobalMinute}
              sx={{ textTransform: 'none', height: '40px' }}
            >
              Zastosuj
            </Button>
          </Box>

          {/* TABELA MECZÓW */}
          <TableContainer component={Paper} sx={{ backgroundColor: '#1e1e1e', borderRadius: 2, border: '1px solid #333', mb: 4, overflowX: 'auto' }}>
			  <Table size="small" sx={{ minWidth: 600, tableLayout: 'fixed', '& th': { color: '#888', fontWeight: 'bold', backgroundColor: '#252525', px: 1 } }}>
				<TableHead>
				  <TableRow>
					<TableCell align="right" sx={{ width: '30%' }}>Gospodarz</TableCell>
					<TableCell align="center" sx={{ width: '15%' }}>Gole (D)</TableCell>
					<TableCell align="center" sx={{ width: '8%' }}>Czer.</TableCell>
					<TableCell align="center" sx={{ width: '8%' }}>Min.</TableCell>
					<TableCell align="center" sx={{ width: '8%' }}>Czer.</TableCell>
					<TableCell align="center" sx={{ width: '15%' }}>Gole (W)</TableCell>
					<TableCell align="left" sx={{ width: '30%' }}>Gość</TableCell>
				  </TableRow>
				</TableHead>
				<TableBody>
				  {Object.entries(localMatches).map(([matchId, m]) => (
					<TableRow key={matchId} sx={{ '& td': { py: 0.5, px: 0.5, borderBottom: '1px solid #333' } }}>
					  <TableCell align="right" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
						<Typography sx={{ color: '#fff', fontSize: '0.85rem', fontWeight: 500 }}>{m.homeTeamName}</Typography>
					  </TableCell>

					  {/* Gole Dom */}
					  <TableCell align="center">
						<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
						  <IconButton size="small" onClick={() => handleIncrement(matchId, 'homeGoals', -1)} sx={{ p: 0 }}><RemoveIcon fontSize="small" /></IconButton>
						  <InputBase value={m.homeGoals} onChange={(e) => handleInputChange(matchId, 'homeGoals', e.target.value)}
							sx={{ width: '30px', height: '24px', backgroundColor: '#2c2c2c', borderRadius: 1, '& input': { textAlign: 'center', p: 0, fontSize: '0.85rem', color: '#fff' } }} 
						  />
						  <IconButton size="small" onClick={() => handleIncrement(matchId, 'homeGoals', 1)} sx={{ p: 0 }}><AddIcon fontSize="small" /></IconButton>
						</Box>
					  </TableCell>

					  {/* Czerwona Dom */}
					  <TableCell align="center">
						<InputBase value={m.homeRed} onChange={(e) => handleInputChange(matchId, 'homeRed', e.target.value)}
						  sx={{ width: '25px', height: '24px', backgroundColor: '#2c2c2c', borderRadius: 1, '& input': { textAlign: 'center', p: 0, fontSize: '0.85rem', color: '#ff4c4c' } }} 
						/>
					  </TableCell>

					  {/* Minuta */}
					  <TableCell align="center">
						<InputBase value={m.minute} onChange={(e) => handleInputChange(matchId, 'minute', e.target.value)}
						  sx={{ width: '30px', height: '24px', backgroundColor: '#2c2c2c', borderRadius: 1, '& input': { textAlign: 'center', p: 0, fontSize: '0.85rem', color: '#4fc3f7' } }} 
						/>
					  </TableCell>

					  {/* Czerwona Wyjazd */}
					  <TableCell align="center">
						<InputBase value={m.awayRed} onChange={(e) => handleInputChange(matchId, 'awayRed', e.target.value)}
						  sx={{ width: '25px', height: '24px', backgroundColor: '#2c2c2c', borderRadius: 1, '& input': { textAlign: 'center', p: 0, fontSize: '0.85rem', color: '#ff4c4c' } }} 
						/>
					  </TableCell>

					  {/* Gole Wyjazd */}
					  <TableCell align="center">
						<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
						  <IconButton size="small" onClick={() => handleIncrement(matchId, 'awayGoals', -1)} sx={{ p: 0 }}><RemoveIcon fontSize="small" /></IconButton>
						  <InputBase value={m.awayGoals} onChange={(e) => handleInputChange(matchId, 'awayGoals', e.target.value)}
							sx={{ width: '30px', height: '24px', backgroundColor: '#2c2c2c', borderRadius: 1, '& input': { textAlign: 'center', p: 0, fontSize: '0.85rem', color: '#fff' } }} 
						  />
						  <IconButton size="small" onClick={() => handleIncrement(matchId, 'awayGoals', 1)} sx={{ p: 0 }}><AddIcon fontSize="small" /></IconButton>
						</Box>
					  </TableCell>

					  <TableCell align="left" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
						<Typography sx={{ color: '#fff', fontSize: '0.85rem', fontWeight: 500 }}>{m.awayTeamName}</Typography>
					  </TableCell>
					</TableRow>
				  ))}
				</TableBody>
			  </Table>
			</TableContainer>

          {/* AKCJE */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <Button
              variant="contained"
              color="primary"
              size="large"
              startIcon={isSimulating ? <CircularProgress size={20} color="inherit" /> : <PlayArrowIcon />}
              onClick={handleSimulate}
              disabled={isSimulating}
              sx={{ px: 5, py: 1.5, fontSize: '1.1rem', fontWeight: 'bold', borderRadius: 3 }}
            >
              {isSimulating ? 'Generowanie...' : 'Uruchom Symulację LIVE (10 000x)'}
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
};

export default LiveSimulation;